import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, Calendar, Tag } from "lucide-react";
import DOMPurify from "dompurify";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const mdToHtml = (md) =>
  md
    .replace(/^## (.*)$/gm, '<h2 class="font-heading font-black text-3xl text-brand-navy mt-10 mb-3">$1</h2>')
    .replace(/^### (.*)$/gm, '<h3 class="font-heading font-bold text-2xl text-brand-navy mt-8 mb-2">$1</h3>')
    .replace(/^> (.*)$/gm, '<blockquote class="border-l-4 border-brand-red pl-4 italic text-brand-navy/80 my-6">$1</blockquote>')
    .replace(/^\- (.*)$/gm, '<li class="ml-6 list-disc text-brand-navy/80">$1</li>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-brand-navy">$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/\n\n/g, '</p><p class="text-brand-navy/80 leading-relaxed mt-4">')
    .replace(/^(?!<)/gm, "");

export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/blog"); setPosts(data); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand-red/15 blur-3xl animate-blob" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">Insights</div>
          <h1 className="mt-3 font-heading font-black text-5xl md:text-6xl text-brand-navy leading-tight">
            The Language Nest <span className="text-brand-red">journal.</span>
          </h1>
          <p className="mt-4 text-brand-navy/60 text-lg max-w-2xl">
            Guides, playbooks and city-specific study advice from Delhi&#39;s most trusted coaching institute.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading ? <Loader2 className="w-8 h-8 animate-spin text-brand-red mx-auto" /> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <motion.div key={p.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="block group">
                    <div className="rounded-3xl bg-white border border-brand-navy/10 p-8 card-hover h-full">
                      <div className="text-xs font-semibold uppercase tracking-widest text-brand-red">{p.tags?.[0] || "Delhi"}</div>
                      <h3 className="mt-3 font-heading font-black text-2xl text-brand-navy group-hover:text-brand-red transition-colors">{p.title}</h3>
                      <p className="mt-3 text-brand-navy/60 text-sm leading-relaxed">{p.excerpt}</p>
                      <div className="mt-6 flex items-center gap-2 text-xs text-brand-navy/40">
                        <Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
              {!loading && posts.length === 0 && <div className="col-span-full text-center text-brand-navy/50 py-16">No posts yet.</div>}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/blog/${slug}`); setPost(data); }
      catch { setErr("Post not found"); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-brand-navy/60 hover:text-brand-red text-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> All posts
        </Link>

        {loading && <Loader2 className="w-8 h-8 animate-spin text-brand-red mt-16 mx-auto" />}
        {err && <div className="mt-16 text-center text-brand-navy/60">{err}</div>}
        {post && (
          <>
            <div className="flex flex-wrap gap-2 mt-8">
              {(post.tags || []).map((t) => (
                <Badge key={t} className="bg-brand-red text-white"><Tag className="w-3 h-3 mr-1" />{t}</Badge>
              ))}
            </div>
            <h1 className="mt-6 font-heading font-black text-4xl md:text-5xl text-brand-navy leading-tight">{post.title}</h1>
            <div className="mt-3 text-brand-navy/50 text-sm">{new Date(post.created_at).toLocaleDateString()}</div>
            <div
              className="mt-10 prose-brand"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  `<p class="text-brand-navy/80 leading-relaxed">${mdToHtml(post.body)}</p>`,
                  { USE_PROFILES: { html: true } }
                ),
              }}
            />

            <div className="mt-14 rounded-3xl bg-brand-red text-white p-8">
              <div className="font-heading font-black text-2xl">Ready to visit our Adarsh Nagar branch?</div>
              <div className="mt-2 text-white/80 text-sm">Call +91 93821 66788 or book a free demo online.</div>
              <Link to="/register" data-testid="blog-cta-btn" className="inline-block mt-4 px-6 py-3 rounded-full bg-white text-brand-red font-semibold btn-scale">Book Free Demo</Link>
            </div>
          </>
        )}
      </article>
      <Footer />
    </div>
  );
}
